/**
 * Unified Spacing System
 *
 * Consistent spacing scale based on 4px increments.
 * Use for margins, padding, gaps, and positioning.
 *
 * @packageDocumentation
 */

// ============================================================================
// Base Spacing Scale (4px increments)
// ============================================================================

/**
 * Spacing values in pixels.
 * Based on 4px grid for visual harmony.
 */
export const spacing = {
  0: 0,
  1: 4,      // 0.25rem
  2: 8,      // 0.5rem
  3: 12,     // 0.75rem
  4: 16,     // 1rem (base)
  5: 20,     // 1.25rem
  6: 24,     // 1.5rem
  8: 32,     // 2rem
  10: 40,    // 2.5rem
  12: 48,    // 3rem
  16: 64,    // 4rem
  20: 80,    // 5rem
  24: 96,    // 6rem
  32: 128,   // 8rem
  40: 160,   // 10rem
  48: 192,   // 12rem
  56: 224,   // 14rem
  64: 256,   // 16rem
} as const

// ============================================================================
// Semantic Spacing
// ============================================================================

/**
 * Named spacing values for common use cases.
 * Use these for consistent component spacing.
 */
export const semanticSpacing = {
  // Component internals
  componentPaddingSm: spacing[2],    // 8px - compact components
  componentPadding: spacing[4],      // 16px - default component padding
  componentPaddingLg: spacing[6],    // 24px - spacious components

  // Between components
  componentGapSm: spacing[2],        // 8px - tight grouping
  componentGap: spacing[4],          // 16px - default gap
  componentGapLg: spacing[8],        // 32px - loose grouping

  // Section spacing
  sectionPaddingSm: spacing[8],      // 32px - compact sections
  sectionPadding: spacing[12],       // 48px - default section padding
  sectionPaddingLg: spacing[16],     // 64px - spacious sections

  // Layout spacing
  layoutMarginSm: spacing[4],        // 16px - tight margins
  layoutMargin: spacing[6],          // 24px - default margins
  layoutMarginLg: spacing[12],       // 48px - spacious margins

  // Interactive spacing (touch targets)
  minTouchTarget: 44,                // 44px - WCAG minimum touch target size
  iconPadding: spacing[2],           // 8px - padding around icons
  buttonPadding: spacing[4],         // 16px - horizontal button padding
  buttonPaddingVertical: spacing[2], // 8px - vertical button padding
} as const

// ============================================================================
// Responsive Spacing Multipliers
// ============================================================================

/**
 * Spacing scale multipliers for different breakpoints.
 * Apply these to base spacing for responsive layouts.
 */
export const responsiveSpacing = {
  mobile: 0.75,    // 75% of base spacing on mobile
  tablet: 1.0,     // Base spacing
  desktop: 1.25,   // 25% more spacing on desktop
  wide: 1.5,       // 50% more spacing on large displays
} as const

// ============================================================================
// Utilities
// ============================================================================

/**
 * Get responsive spacing value
 * @param base - Base spacing value
 * @param breakpoint - Target breakpoint
 */
export function getResponsiveSpacing(
  base: number,
  breakpoint: keyof typeof responsiveSpacing
): number {
  return base * responsiveSpacing[breakpoint]
}

/**
 * Calculate spacing for a specific multiplier
 * @param base - Base spacing key
 * @param multiplier - Multiplier value
 */
export function scaleSpacing(
  base: keyof typeof spacing,
  multiplier: number
): number {
  return spacing[base] * multiplier
}
